// Package imports:
import React from 'react';
import { connect, ConnectedProps } from 'react-redux';
// Style imports:
// Component imports:
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import DatePickerRange from '../common/DatePickerRange';
// Service imports:
import { formatDate, getDatePair } from '../../services/utils';
// Action imports:
import { requestBrokenTradeReports } from '../../redux-store/actions/reportActions';
// Type imports:
import { RootState } from '../../redux-store/reducers';
import StickyHeaderTable from '../common/StickyHeaderTable';
import { exportToExcel } from './summaryPerClientOrTrader/helperFunctions';
import SortFilterTable from '../common/SortFilterTable';

//@ts-ignore
window.$.DataTable = require('datatables.net')

type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps;

interface IState {
    tableData: { timestamp: string; type: string; side: string | null; counterparty: string | null; symbol: string; status: string; trader_id: string | null; }[];
}

class ReportOfBrokenTradeReports extends React.Component<Props, IState> {
    private dataTableInstance: any;

    constructor(props: Props) {
        super(props);
        this.state = {
            tableData: [],
        };
        this.dataTableInstance = null;
    }

    isLoted = false;
    isError = false;

    componentDidMount() {
        this.initializeDataTable();
        this.fetchData();
    }
    componentDidUpdate(prevProps: Props) {
        if (this.props.fromDate !== prevProps.fromDate || this.props.toDate !== prevProps.toDate) {
            this.fetchData();
        }
        this.updateDataTable();
    }
    componentWillUnmount() {
        if (this.dataTableInstance) {
            this.dataTableInstance?.destroy();
        }
    }
    initializeDataTable = () => {
        // @ts-ignore
        this.dataTableInstance = window.$('#reportTable').DataTable({
            orderCellsTop: true,
            fixedHeader: false,
            pageLength: 100,
            scrollCollapse: true,
            scroller: true,
            deferRender: true,
            paging: false,
            info: false,
            searching: true,
            order: [[0, 'asc']],
            bLengthChange: false,
            dom: 'Bfrtip',
            buttons: ['copy', 'csv', 'excel', 'pdf', 'print']
        });
        $('.dt-button').removeClass('dt-button buttons-html5').addClass('btn-dark btn btn-sm');
    }

    // ===================================== Fetch Data From API =====================================
    fetchData = () => {
        const { fromDate, toDate, accessTokenV2, hasPrivateData, brokenTradeReportsMap, requestBrokenTradeReports } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const brokenTradeReportsInfo = brokenTradeReportsMap[datePair];
        if (brokenTradeReportsInfo === undefined && hasPrivateData) {
            requestBrokenTradeReports(fromDate, toDate, accessTokenV2);
        }
        this.isLoted = true;
    }

    // ===================================== Update Data Table Info =====================================
    updateDataTable = () => {
        const { brokenTradeReportsMap, fromDate, toDate } = this.props;
        const datePair = getDatePair(fromDate, toDate);
        const brokenTradeReportsInfo = brokenTradeReportsMap[datePair];
        // ----------------- If Error loading data -----------------
        if (brokenTradeReportsInfo instanceof Error) {
            this.isError = true;
        }
        // ----------------- If data is still loading -----------------
        else if (brokenTradeReportsInfo === 'waiting') {
            this.isLoted = true;
        }
        // ----------------- If data is loaded -----------------
        else if (this.isLoted && Array.isArray(brokenTradeReportsInfo)) {
            const tableData = brokenTradeReportsInfo.map((tradeReport) => ({
                timestamp: formatDate(tradeReport.timestamp),
                type: tradeReport.tickdata_type,
                side: tradeReport.side,
                counterparty: tradeReport.counterparty,
                symbol: tradeReport.symbol || '', // Ensure symbol is always a string
                status: tradeReport.status,
                trader_id: tradeReport.trader_id
            }));
            this.isLoted = false;
            this.setState({ tableData });
        }
    }

    render () {
        const { tableData } = this.state;

        return (
            <>
                <Row className='SecondaryNavBar'>
                    <Col xs={12} className='DatePickerRangeCol'>
                        <DatePickerRange />
                    </Col>
                </Row>
                <Row className='TableTitle'>
                    <Col xs={12} className='TableTitle-col'>
                        <h5 className='SectionTitle'>Broken trade reports</h5> 
                        <button 
                            id='excelButton' 
                            onClick={exportToExcel} 
                            className='VerySmallButton btn btn-dark btn-sm'
                        >Excel</button>
                    </Col>
                </Row>
                <Row className='TableRow'>
                    <Col xs={12} className='TableCol'>
                        <StickyHeaderTable hideScrollbar={false}>
                            <SortFilterTable
                                data={tableData}
                                isLoading={this.isLoted}
                                isError={this.isError}
                                rowId={({ timestamp }) => timestamp ?? undefined}
                                columns={[
                                    {
                                        title: 'Timestamp',
                                        textAlign: 'left',
                                        dataAccessor: ({ timestamp }) => timestamp,
                                        simpleSortable: ({ timestamp }) => timestamp,
                                        filter: {
                                            id: 'Timestamp',
                                            type: 'string',
                                            stringValue: ({ timestamp }) => timestamp
                                        }
                                    },
                                    {
                                        title: 'Type',
                                        textAlign: 'left',
                                        dataAccessor: ({ type }) => type,
                                        simpleSortable: ({ type }) => type,
                                    },
                                    {
                                        title: 'Side',
                                        textAlign: 'left',
                                        dataAccessor: ({ side }) => side,
                                        simpleSortable: ({ side }) => side,
                                        filter: {
                                            id: 'Side',
                                            type: 'string',
                                            stringValue: ({ side }) => side
                                        }
                                    },
                                    {
                                        title: 'Counterparty',
                                        textAlign: 'left',
                                        dataAccessor: ({ counterparty }) => counterparty,
                                        simpleSortable: ({ counterparty }) => counterparty,
                                        filter: {
                                            id: 'Counterparty',
                                            type: 'string',
                                            stringValue: ({ counterparty }) => counterparty
                                        }
                                    },
                                    {
                                        title: 'Symbol',
                                        textAlign: 'left',
                                        dataAccessor: ({ symbol }) => symbol,
                                        simpleSortable: ({ symbol }) => symbol,
                                        filter: {
                                            id: 'Symbol',
                                            type: 'string',
                                            stringValue: ({ symbol }) => symbol
                                        }
                                    },
                                    {
                                        title: 'Status',
                                        textAlign: 'left',
                                        dataAccessor: ({ status }) => status,
                                        simpleSortable: ({ status }) => status,
                                    },
                                    {
                                        title: 'Trader ID',
                                        textAlign: 'left',
                                        dataAccessor: ({ trader_id }) => trader_id,
                                        simpleSortable: ({ trader_id }) => trader_id,
                                        filter: {
                                            id: 'Trader ID',
                                            type: 'string',
                                            stringValue: ({ trader_id }) => trader_id
                                        }
                                    },
                                ]}
                            />
                        </StickyHeaderTable>
                    </Col>
                </Row>
            </>
        );
    }
}

function mapStateToProps({ dashboardReducer, reportReducer, loginReducer }: RootState) {
    const { dayRange } = dashboardReducer;
    const { fromDate, toDate } = dayRange;
    const { accessTokenV2, hasPrivateData } = loginReducer;
    const { brokenTradeReportsMap } = reportReducer;
    return {
        fromDate,
        toDate,
        accessTokenV2,
        hasPrivateData,
        brokenTradeReportsMap
    };
}

const connector = connect(mapStateToProps, { requestBrokenTradeReports });
export default connector(ReportOfBrokenTradeReports);


